// Distance-based location matching for the Recruiter AI Match tab.
//
// KERALA_PLACES_GEO holds real geocoded lat/lng (one-time offline lookup, built
// from OpenStreetMap/Nominatim) for every town name the registration/job forms
// let people pick or type. KERALA_DISTRICT_CENTROIDS is a coarser fallback used
// when a specific town can't be resolved but the district is known.
import KERALA_PLACES_GEO from './kerala-places-geo.json';

export const KERALA_DISTRICT_CENTROIDS = {
  'Thiruvananthapuram': { lat: 8.5241, lon: 76.9366 },
  'Kollam': { lat: 8.8932, lon: 76.6141 },
  'Pathanamthitta': { lat: 9.2648, lon: 76.7870 },
  'Alappuzha': { lat: 9.4981, lon: 76.3388 },
  'Kottayam': { lat: 9.5916, lon: 76.5222 },
  'Idukki': { lat: 9.8560, lon: 76.9376 },
  'Ernakulam': { lat: 9.9816, lon: 76.2999 },
  'Thrissur': { lat: 10.5276, lon: 76.2144 },
  'Palakkad': { lat: 10.7867, lon: 76.6548 },
  'Malappuram': { lat: 11.0510, lon: 76.0711 },
  'Kozhikode': { lat: 11.2588, lon: 75.7804 },
  'Wayanad': { lat: 11.6854, lon: 76.1320 },
  'Kannur': { lat: 11.8745, lon: 75.3704 },
  'Kasaragod': { lat: 12.4996, lon: 74.9869 }
};

const normalize = (s) => String(s || '').toLowerCase().trim().replace(/[^a-z0-9\s]/g, '');

const PLACE_KEYS = Object.keys(KERALA_PLACES_GEO).filter((k) => KERALA_PLACES_GEO[k]);
const DISTRICT_KEYS = Object.keys(KERALA_DISTRICT_CENTROIDS);

export function findPlaceCoords(text) {
  const norm = normalize(text);
  if (!norm) return null;
  for (const key of PLACE_KEYS) {
    if (normalize(key) === norm) return KERALA_PLACES_GEO[key];
  }
  let best = null, bestLen = 0;
  for (const key of PLACE_KEYS) {
    const nk = normalize(key);
    if (nk.length >= 4 && (norm.includes(nk) || nk.includes(norm)) && nk.length > bestLen) {
      bestLen = nk.length;
      best = key;
    }
  }
  return best ? KERALA_PLACES_GEO[best] : null;
}

export function findDistrictCoords(text) {
  const norm = normalize(text);
  if (!norm) return null;
  for (const key of DISTRICT_KEYS) {
    const nk = normalize(key);
    if (nk === norm || norm.includes(nk) || nk.includes(norm)) return KERALA_DISTRICT_CENTROIDS[key];
  }
  return null;
}

export function haversineKm(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// Extracts lat/lng from the "https://maps.google.com/?q=LAT,LON" links saved by
// the HR Assistant's map picker (StaffDashboard.js google_location field).
// Also accepts "@LAT,LON" / "ll=LAT,LON" map links and plain "LAT, LON" text pasted by a recruiter.
export function parsePinnedLocation(googleLocation) {
  const text = String(googleLocation || '');
  const m = text.match(/(?:[?&](?:q|ll|query)=|@)(-?\d+\.?\d*),\s*(-?\d+\.?\d*)/) || text.match(/^\s*(-?\d{1,2}\.\d+)\s*,\s*(-?\d{1,3}\.\d+)\s*$/);
  if (!m) return null;
  const lat = parseFloat(m[1]), lon = parseFloat(m[2]);
  return Number.isFinite(lat) && Number.isFinite(lon) && Math.abs(lat) <= 90 && Math.abs(lon) <= 180 ? { lat, lon } : null;
}

// Town and district names the geo lookup knows — location suggestions in the AI Match filters.
export const KNOWN_PLACE_NAMES = [...new Set([...PLACE_KEYS, ...DISTRICT_KEYS])].sort((a, b) => a.localeCompare(b));

// Resolves whatever a recruiter typed (map link, coordinates, town or district) to a point.
export function resolvePointFromText(text) {
  const pinned = parsePinnedLocation(text);
  if (pinned) return { ...pinned, precision: 'exact', label: 'Pinned location' };
  const placeC = findPlaceCoords(text);
  if (placeC) return { ...placeC, precision: 'town', label: 'Town-level estimate' };
  const distC = findDistrictCoords(text);
  if (distC) return { ...distC, precision: 'district', label: 'District-level estimate' };
  return null;
}

const firstOf = (v) => (Array.isArray(v) ? v[0] : String(v || '').split(',')[0]);

export function resolveMemberCoords(reg) {
  if (!reg) return null;
  const pinned = parsePinnedLocation(reg.google_location);
  if (pinned) return { ...pinned, precision: 'exact', label: 'Pinned location' };

  const placeC = findPlaceCoords(reg.place) || findPlaceCoords(firstOf(reg.preferred_district)) || findPlaceCoords(reg.preferred_location);
  if (placeC) return { ...placeC, precision: 'town', label: 'Town-level estimate' };

  const distC = findDistrictCoords(reg.district) || findDistrictCoords(firstOf(reg.preferred_district));
  if (distC) return { ...distC, precision: 'district', label: 'District-level estimate' };

  return null;
}

export function resolveVacancyCoords(vac) {
  if (!vac) return null;
  const pinned = parsePinnedLocation(vac.company_map);
  if (pinned) return { ...pinned, precision: 'exact', label: 'Company map pin' };

  const placeC = findPlaceCoords(vac.location);
  if (placeC) return { ...placeC, precision: 'town', label: 'Town-level estimate' };

  const distC = findDistrictCoords(vac.district) || findDistrictCoords(vac.location);
  if (distC) return { ...distC, precision: 'district', label: 'District-level estimate' };

  return null;
}

// Returns null when either side can't be geo-resolved at all (unknown place names).
export function getDistanceInfo(reg, vac) {
  const a = resolveMemberCoords(reg);
  const b = resolveVacancyCoords(vac);
  if (!a || !b) return null;
  const km = haversineKm(a.lat, a.lon, b.lat, b.lon);
  const approx = a.precision === 'district' || b.precision === 'district';
  return { km: Math.round(km * 10) / 10, approx };
}

// True when the member explicitly named this vacancy's location/district as a
// preference — a stronger, more intentional signal than mere proximity.
export function isPreferredLocationMatch(reg, vac) {
  const vacText = normalize(`${vac?.location || ''} ${vac?.district || ''}`);
  if (!vacText) return false;
  const prefLoc = normalize(reg?.preferred_location);
  if (prefLoc && prefLoc.length >= 4 && (vacText.includes(prefLoc) || prefLoc.includes(vacText))) return true;
  const prefDistricts = Array.isArray(reg?.preferred_district)
    ? reg.preferred_district
    : String(reg?.preferred_district || '').split(',').map((s) => s.trim()).filter(Boolean);
  return prefDistricts.some((d) => {
    const nd = normalize(d);
    return nd && nd.length >= 4 && (vacText.includes(nd) || nd.includes(vacText));
  });
}
